import Header from 'components/header/Header';
import React, { useCallback, useEffect, useState } from 'react';
import PlateDefinition from './PlateDefinition';
import PlateDrops from './PlateDrops';
import { PlateTypesInfo } from './types';
import { usePlateTypes } from './hooks';
import {
  PlateTypeCountErrorMsg,
  checkPlateTypesCount,
  createNewDrop,
  getModifiedContainers,
  validatePlateTypes,
} from './utils';
import { SaveContainerDTO, SavePlateTypeDTO } from 'business/models/Models';
import PlateTypeService from 'business/services/PlateTypeService';
import { IStyle } from 'Styles';
import RMModal from 'components/modal/RMModal';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { KeyboardKeys } from 'constants/';
import { useSaveDiscardButtons } from 'CustomHooks/useSaveDiscardButtons';

const plateTypeService = new PlateTypeService();
const PlateTypesDashboard = () => {
  const [isDirty, setIsDirty] = useState(false);
  const [plateTypeErrors, setPlateTypeErrors] = useState<PlateTypesInfo>({});
  const [isPlateTypeCountError, setIsPlateTypeCountError] = useState(false);
  const [plateCountErrorObj, setPlateCountErrorObj] = useState<PlateTypeCountErrorMsg>(
    {},
  );
  const [selectedPlateTypeIndices, setSelectedPlateTypeIndices] = useState<number[]>([]);
  const [selectedPlateTypeIds, setSelectedPlateTypeIds] = useState<number[]>([]);
  const [deletedPlateTypeIds, setDeletedPlateTypeIds] = useState([]);
  const [deletedDropIds, setDeletedDropIds] = useState([]);
  const [editId, setEditId] = useState<number>(null);
  const [disabledDropIds, setDisabledDropIds] = useState<number[]>([]);
  const [isNumDropsBlurToggle, setIsNumDropsBlurToggle] = useState(false);
  const {
    originalPlateTypes,
    setOriginalPlateTypes,
    currentPlateTypes,
    setCurrentPlateTypes,
    previousDropNums,
    setPreviousDropNums,
  } = usePlateTypes();

  const resetPageState = () => {
    setDeletedPlateTypeIds([]);
    setDeletedDropIds([]);
    setDisabledDropIds([]);
    setSelectedPlateTypeIds([]);
    setSelectedPlateTypeIndices([]);
    setPlateTypeErrors({});
    setIsDirty(false);
    setEditId(null);
    setPlateCountErrorObj({});
  };

  const handleChangeInDropNum = (changeIndex) => {
    //Return when new plate type is being created
    if (
      changeIndex === undefined ||
      changeIndex === null ||
      changeIndex === previousDropNums.length
    ) {
      return;
    }
    const selectedItem = currentPlateTypes[changeIndex];
    const updatedDropNum = selectedItem.maxNumDrops;
    if (updatedDropNum.toString().trim().length === 0) {
      return;
    }
    const dropChange =
      parseInt(updatedDropNum.toString()) -
      parseInt(previousDropNums[changeIndex].toString());

    if (dropChange > 0) {
      setCurrentPlateTypes((prev) => {
        const updatedPrev = [...prev];
        const prevItem = updatedPrev[changeIndex];
        const updatedItem = new SaveContainerDTO(updatedPrev[changeIndex]);
        const newContainerDrops = [...prevItem.containerDrops];
        const dropsNum = updatedItem.containerDrops.length;
        new Array(dropChange).fill(null).forEach((val, idx) => {
          newContainerDrops.push(createNewDrop(dropsNum + idx + 1, updatedItem.id));
        });
        updatedItem.containerDrops = [...newContainerDrops];
        updatedPrev[changeIndex] = updatedItem;
        return updatedPrev;
      });
    } else if (dropChange < 0) {
      const nullArray = new Array(Math.abs(parseInt(updatedDropNum.toString()))).fill(
        null,
      );
      setCurrentPlateTypes((prev) => {
        const updatedPrev = [...prev];
        const prevItem = updatedPrev[changeIndex];
        const updatedItem = new SaveContainerDTO(updatedPrev[changeIndex]);
        updatedItem.containerDrops = [...prevItem.containerDrops];
        const newContainerDrops = [];
        nullArray.forEach((val, index) => {
          const dropIndex = updatedItem.containerDrops.findIndex(
            (drop) => drop.dropNumber === index + 1,
          );
          if (dropIndex === -1) {
            const newDrop = createNewDrop(index + 1, updatedItem.id);
            newContainerDrops.push(newDrop);
          } else {
            newContainerDrops.push(updatedItem.containerDrops[dropIndex]);
          }
        });
        // Find the elements unique to updatedItem.containerDrops and return their ids as array
        setDeletedDropIds(
          updatedItem.containerDrops
            .filter((obj1) => !newContainerDrops.some((obj2) => obj2.id === obj1.id))
            .map((obj) => obj.id),
        );
        updatedItem.containerDrops = [...newContainerDrops];
        updatedPrev[changeIndex] = updatedItem;
        return updatedPrev;
      });
    }
    setPreviousDropNums((prev) => {
      const updatedPrev = [...prev];
      updatedPrev[changeIndex] = updatedDropNum;
      return updatedPrev;
    });
  };

  const onSave = () => {
    handleChangeInDropNum(selectedPlateTypeIndices[0]);
    const plateErrorMessage: PlateTypeCountErrorMsg =
      checkPlateTypesCount(currentPlateTypes);
    if (plateErrorMessage.isCountValid === false) {
      setIsPlateTypeCountError(true);
      setPlateCountErrorObj(plateErrorMessage);
      return;
    }
    const plateTypesRowInfo: PlateTypesInfo = {};
    currentPlateTypes.forEach((plateType, _idx) => {
      const originalPlate = originalPlateTypes.find((x) => x.id === plateType.id);
      const plateTypesErrors = validatePlateTypes(
        plateType,
        currentPlateTypes,
        _idx,
        originalPlate,
      );
      Object.assign(plateTypesRowInfo, plateTypesErrors);
    });
    if (Object.keys(plateTypesRowInfo).length) {
      setPlateTypeErrors(plateTypesRowInfo);
    } else {
      setPlateTypeErrors({});
      const newSavePlateTypeDTO = new SavePlateTypeDTO();
      newSavePlateTypeDTO.deleteContainerIds = deletedPlateTypeIds;
      newSavePlateTypeDTO.deleteContainerDropsIds = deletedDropIds;
      newSavePlateTypeDTO.saveContainerDTOs = getModifiedContainers(
        currentPlateTypes,
        originalPlateTypes,
      );
      plateTypeService
        .saveAllPlateTypes(newSavePlateTypeDTO)
        .then((updatedDto) => {
          setCurrentPlateTypes(updatedDto.saveContainerDTOs);
          setOriginalPlateTypes(updatedDto.saveContainerDTOs);
          setPreviousDropNums(
            updatedDto.saveContainerDTOs.map((plateType) => plateType.maxNumDrops),
          );
          showAlertSnackBar(EAlertType.INFO, ESnackBarPosition.TOP, 'Plate Types saved.');
        })
        .catch((error) => {
          const errMsg =
            error?.message || error?.ErrorMessage || 'Error saving Plate Types.';
          showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, errMsg);
        });
      resetPageState();
    }
  };

  const onDiscard = () => {
    setCurrentPlateTypes([...originalPlateTypes]);
    setPreviousDropNums(originalPlateTypes.map((plateType) => plateType.maxNumDrops));
    resetPageState();
  };

  const saveDiscardButtons = useSaveDiscardButtons({
    enableButtons: isDirty,
    onSave: onSave,
    onDiscard: onDiscard,
  });

  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const ctrlKey = e.ctrlKey || e.metaKey;

      if (ctrlKey && e.key === KeyboardKeys.S) {
        e.preventDefault();
        if (isDirty) {
          onSave();
        }
      }
    },
    [isDirty, onSave],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener('keydown', handleUserKeyPress, { signal });

    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);
  return (
    <>
      <div id="platetypesdashboard-container" className="w-full h-full">
        <div id="platetypesdashboard-header-container" className="setup-bg-topbar">
          <Header
            headerButtons={saveDiscardButtons}
            isNewTheme={true}
            title={'Plate Types'}
          />
          <div
            id="platetypesdashboard-plateinfo-wrapper"
            className="bg-white p-[4px]"
            style={styles.plateInfoContainer}
          >
            <div
              id="platetypesdashboard-plateinfo-content"
              className="flex gap-[8px] border border-[color:var(--neutral-color-neutral-10,#E0E0E0)] border-solid"
            >
              <PlateDefinition
                currentPlateTypes={currentPlateTypes}
                setCurrentPlateTypes={setCurrentPlateTypes}
                selectedIds={selectedPlateTypeIds}
                setSelectedIds={setSelectedPlateTypeIds}
                selectedIndices={selectedPlateTypeIndices}
                setSelectedIndices={setSelectedPlateTypeIndices}
                deletedPlateTypeIds={deletedPlateTypeIds}
                setDeletedPlateTypeIds={setDeletedPlateTypeIds}
                setIsDirty={setIsDirty}
                deletedDropIds={deletedDropIds}
                setDeletedDropIds={setDeletedDropIds}
                plateTypeErrors={plateTypeErrors}
                editId={editId}
                setEditId={setEditId}
                previousDropNums={previousDropNums}
                setPreviousDropNums={setPreviousDropNums}
                handleChangeInDropNum={handleChangeInDropNum}
                setIsNumDropsBlurToggle={setIsNumDropsBlurToggle}
              />
              <div
                id="platetypesdashboard-divider"
                className="bg-[#CACACA] w-[1px]"
              ></div>
              {selectedPlateTypeIndices.length > 0 && (
                <PlateDrops
                  selectedIndices={selectedPlateTypeIndices}
                  currentPlateTypes={currentPlateTypes}
                  setCurrentPlateTypes={setCurrentPlateTypes}
                  deletedDropIds={deletedDropIds}
                  setDeletedDropIds={setDeletedDropIds}
                  setIsDirty={setIsDirty}
                  dummyDropIds={disabledDropIds}
                  setDummyDropIds={setDisabledDropIds}
                  editId={editId}
                  isNumDropsBlurToggle={isNumDropsBlurToggle}
                />
              )}
            </div>
          </div>
        </div>
        <RMModal
          id="platetypesdashboard-errormodal"
          showModal={isPlateTypeCountError}
          setShowModal={setIsPlateTypeCountError}
          onOk={() => {
            setIsPlateTypeCountError(false);
            setPlateCountErrorObj({});
          }}
          body={`${plateCountErrorObj.msg}`}
          isOkOnly={true}
          heading={`Error`}
          okButtonText={'Ok'}
        />
      </div>
    </>
  );
};

export default PlateTypesDashboard;

const styles: IStyle = {
  plateInfoContainer: {
    width: '91vw',
    height: '81vh',
  },
};
